import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useMemo, useRef, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Quiz } from "@domain/lms/quiz/quiz";
import { QuizDraft } from "@domain/lms/quiz/quizDraft";
import { useEffectAsync, useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { PublishQuizModal } from "./publishQuizModal";
import { QuizDetailsEditor } from "./quizDetailsEditor";
import { QuizEditorController } from "@ui/pages/lms/quiz/quizEditorPage/quizEditorController/quizEditorController";
import { QuizEditorControllerContext } from "@ui/pages/lms/quiz/quizEditorPage/quizEditorController/quizEditorControllerContext";
import { QuizMainEditor } from "./quizMainEditor";

interface QuizEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialQuiz: QuizDraft;
	step: "MAIN" | "DETAILS";
	onStepChange: (step: "MAIN" | "DETAILS") => void;
	quizId?: string;
}
export const QuizEditor: React.FC<QuizEditorProps> = ({ initialQuiz, quizId, step, onStepChange, ...htmlProps }) => {
	const controller = useMemo(() => new QuizEditorController(initialQuiz), []);
	const quiz = useObservable(controller.quiz);
	const { quizService, trackingService } = useServices();
	const previousStatus = useRef(quiz.status);
	const isMobile = useIsMobile();
	const history = useHistory();
	const { createModal } = useModalTools();
	const [loading, setLoading] = useState(false);

	const saveQuiz = async (
		track?: "track",
		toast?: "toast",
		redirect?: "root" | "editor"
	): Promise<Quiz | undefined> => {
		setLoading(true);
		const currentQuiz = controller.quiz.get();
		try {
			if (track) {
				trackingService.track("quiz / save", { status: currentQuiz.status });
			}
			const fetchedQuiz = await quizService.saveQuiz(currentQuiz, quizId, currentQuiz.publisher?.id);
			if (toast) {
				createToast(I18n.t("success"), I18n.t("quiz.save.success"), "success");
			}
			if (redirect === "root") {
				history.push(Path.Root);
			}
			if (redirect === "editor") {
				history.replace(Path.QuizEditor(fetchedQuiz.id));
			}
			return fetchedQuiz;
		} catch (e) {
			console.error(e);
			createToast(I18n.t("quiz.toast.save.title"), I18n.t("quiz.toast.save.error"), "error");
			return undefined;
		} finally {
			setLoading(false);
		}
	};

	useEffectAsync(async () => {
		if (previousStatus.current !== quiz.status) {
			const { confirmed, shouldShare, shareMessage } =
				quiz.status === "PUBLISHED"
					? await new Promise<{ confirmed: boolean; shouldShare: boolean; shareMessage?: ContentState }>((resolve) =>
							createModal((close) => (
								<PublishQuizModal
									quiz={quiz}
									onConfirm={(confirmed, shouldShare, shareMessage) => {
										close();
										resolve({
											confirmed,
											shouldShare,
											shareMessage,
										});
									}}
								/>
							))
						)
					: {
							confirmed: true,
							shouldShare: false,
							shareMessage: undefined,
						};
			if (!confirmed) {
				controller.setStatus(previousStatus.current);
				return;
			}

			trackingService.track(`quiz / ${quiz.status === "PUBLISHED" ? "publish" : "archive"}`);
			const savedQuiz = await saveQuiz();
			if (savedQuiz) {
				createToast(
					I18n.t("success"),
					I18n.t(quiz.status === "PUBLISHED" ? "quiz.publish.success" : "quiz.archive.success"),
					"success"
				);
				if (shouldShare) {
					await quizService.shareQuiz(savedQuiz.id, shareMessage);
					trackingService.track("quiz / share from publish");
				}
				previousStatus.current = quiz.status;
				history.push(Path.Root);
			} else {
				controller.setStatus(previousStatus.current);
			}
		}
	}, [quiz.status]);

	return (
		<QuizEditorControllerContext.Provider value={controller}>
			<Container {...htmlProps}>
				{isMobile ? (
					step === "MAIN" ? (
						<>
							<QuizMainEditor />
							<PrimaryButton onClick={() => onStepChange("DETAILS")} disabled={!controller.isValidForNextStep()}>
								{I18n.t("next")}
							</PrimaryButton>
						</>
					) : (
						<QuizDetailsEditor onSaveQuiz={saveQuiz} loading={loading} quizId={quizId} />
					)
				) : (
					<>
						<StyledMainEditor />
						<StyledDetailsEditor onSaveQuiz={saveQuiz} loading={loading} quizId={quizId} />
					</>
				)}
			</Container>
		</QuizEditorControllerContext.Provider>
	);
};

const Container = styled.div`
	width: 100%;
	@media ${desktop} {
		display: grid;
		grid-gap: 20px;
		grid-template-columns: auto 348px;
		grid-template-rows: auto;
		grid-template-areas:
			"main details"
			"main details"
			"main details";
	}
	@media ${mobile} {
		${gap(40)};
		> button {
			margin: 0 auto;
		}
	}
`;

const StyledMainEditor = styled(QuizMainEditor)`
	grid-area: main;
`;

const StyledDetailsEditor = styled(QuizDetailsEditor)`
	grid-area: details;
`;
